
import React, { useCallback, useState } from 'react';
import { LFC } from '../../../Legacy';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { ActiveObject, resolve, useResolve } from '../../Container';
import { AccountModel } from '../../account/AccountModel';
import { EditorModeInfo } from '../../canvas/EditorModeInfo';
import { EditorSession, useEditorSessionObserver } from '../../canvas/EditorSession';
import { MyRoleInfo } from '../../canvas/MyRoleInfo';
import { ViewMode } from '../../canvas/workflow/ViewMode';
import { PopoutWidget } from '../../canvas/workspace/widgets/WidgetInterface';
import { WidgetManager } from '../../canvas/workspace/widgets/WidgetManager';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { LensInfo } from './LensInfo';
import { CreateOrEditLensView } from './ui/CreateOrEditLensView';
import { DescribeLensView } from './ui/DescribeLensView';

export class LensWidgetMechanics extends ActiveObject {

    @resolve(AccountModel) private account!: AccountModel;
    @resolve(CommandDispatcher) private dispatch!: CommandDispatcher;
    @resolve(EditorSession) private session!: EditorSession;
    @resolve(WidgetManager) private widgets!: WidgetManager;

    public start(): void {
        this.burden(
            this.session.observe(MyRoleInfo, this.realizeWidget),
            this.session.observe(EditorModeInfo, this.realizeWidget),
        );
        this.realizeWidget();
    }

    private realizeWidget = () => {
        const { account, dispatch, session, widgets } = this;
        const { canParticipate } = session.get(MyRoleInfo);
        const mode = session.get(EditorModeInfo);

        widgets.delete('lens');

        if (!account.plan.features.lensesBeta) return;
        if (!canParticipate) return;
        if (mode.id === ViewMode.id) return;

        const lens = widgets.createContainer({
            name: 'lens',
            weight: 400,
            position: ['docked', 'bottomLeft', 'inline'],
        });

        lens.add(widgets.createPopout({
            image: 'deqo://icons/i8/monocle',
            text: 'Lenses',
            ui: LensWidget,
        }));

        widgets.add(lens);
    };
}

type LensWidgetView = 'view' | 'edit';

export const LensWidget: LFC<{ widget: PopoutWidget }> = ({ widget }) => {

    const session = useResolve(EditorSession);
    const current = useEditorSessionObserver(LensInfo);

    const [view, setView] = useState<LensWidgetView>(current === LensInfo.empty ? 'edit' : 'view');

    const onCreate = (newLens: LensInfo) => {
        session.update(LensInfo, newLens);
        setView('view');
    };

    const onAction = useCallback((action: string, newLens?: LensInfo) => {
        if (action == 'save' && !!newLens) {
            session.update(LensInfo, newLens);
            widget.update({ view: 'view' });
        }
        else if (action === 'edit') {
            setView('edit');
            widget.update({ view: 'edit' });
        }
        else if (action === 'remove') {
            session.update(LensInfo, LensInfo.empty);
            setView('view');
            widget.update({ open: false });
        }
    }, [widget]);

    return <Flexer minWidth="20">
        {view === 'view' && <DescribeLensView lens={current} onAction={onAction} />}
        {view === 'edit' && <CreateOrEditLensView lens={current} onCreate={onCreate} />}
    </Flexer>;
};
